import { useEffect, useState } from "react";
import { PageHeader, useResource } from "@/admin/shared";
import { Button, Field, Input, Select } from "@/admin/ui";
import { api, formatApiError } from "@/admin/api";

export default function Settings() {
    const { data, loading, refresh } = useResource("/settings");
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => {
        if (data) setForm({ ...data });
    }, [data]);

    async function save(e) {
        e?.preventDefault?.();
        setBusy(true);
        setErr("");
        setMsg("");
        try {
            const payload = {
                platform_name: form.platform_name,
                logo_url: form.logo_url,
                default_currency: form.default_currency,
                default_plan: form.default_plan,
                password_min_length: Number(form.password_min_length) || 8,
                session_timeout_hours: Number(form.session_timeout_hours) || 12,
                ai_daily_limit: Number(form.ai_daily_limit) || 100,
                email_from: form.email_from,
                notify_new_signup: !!form.notify_new_signup,
                notify_billing: !!form.notify_billing,
            };
            await api.patch("/settings", payload);
            setMsg("Settings saved.");
            await refresh();
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    if (loading || !form) {
        return (
            <div data-testid="admin-settings-page">
                <PageHeader title="Settings" />
                <div className="glass rounded-3xl p-8 h-[400px] animate-pulse" />
            </div>
        );
    }

    return (
        <div data-testid="admin-settings-page">
            <PageHeader
                title="Settings"
                subtitle="Platform-wide configuration for branding, security, AI usage, email, and notifications."
                action={
                    <Button size="lg" onClick={save} disabled={busy} data-testid="settings-save-btn">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                }
            />

            {msg && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 text-emerald-700 text-sm px-4 py-3 mb-5">
                    {msg}
                </div>
            )}
            {err && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-5">
                    {err}
                </div>
            )}

            <form onSubmit={save} className="space-y-5">
                <Section title="Brand">
                    <Field label="Platform Name"><Input value={form.platform_name || ""} onChange={(e) => setForm({ ...form, platform_name: e.target.value })} /></Field>
                    <Field label="Logo URL"><Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} /></Field>
                </Section>

                <Section title="Defaults">
                    <Field label="Default Currency"><Input value={form.default_currency || ""} onChange={(e) => setForm({ ...form, default_currency: e.target.value })} /></Field>
                    <Field label="Default Plan">
                        <Select value={form.default_plan || "trial"} onChange={(e) => setForm({ ...form, default_plan: e.target.value })}>
                            {["trial", "starter", "growth", "enterprise"].map((p) => <option key={p}>{p}</option>)}
                        </Select>
                    </Field>
                </Section>

                <Section title="Security">
                    <Field label="Password minimum length"><Input type="number" min="6" value={form.password_min_length || 10} onChange={(e) => setForm({ ...form, password_min_length: e.target.value })} /></Field>
                    <Field label="Session timeout (hours)"><Input type="number" min="1" value={form.session_timeout_hours || 12} onChange={(e) => setForm({ ...form, session_timeout_hours: e.target.value })} /></Field>
                </Section>

                <Section title="AI & Usage">
                    <Field label="AI daily call limit"><Input type="number" min="0" value={form.ai_daily_limit || 0} onChange={(e) => setForm({ ...form, ai_daily_limit: e.target.value })} /></Field>
                </Section>

                <Section title="Email & Notifications">
                    <Field label="Email sender"><Input value={form.email_from || ""} onChange={(e) => setForm({ ...form, email_from: e.target.value })} placeholder="noreply@ceosgpt.ai" /></Field>
                    <div className="space-y-2 col-span-full sm:col-span-1">
                        <ToggleRow
                            label="Notify me on new sign-ups"
                            value={!!form.notify_new_signup}
                            onChange={(v) => setForm({ ...form, notify_new_signup: v })}
                            testid="setting-notify-signup"
                        />
                        <ToggleRow
                            label="Notify me on billing events"
                            value={!!form.notify_billing}
                            onChange={(v) => setForm({ ...form, notify_billing: v })}
                            testid="setting-notify-billing"
                        />
                    </div>
                </Section>
            </form>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="glass rounded-3xl p-6 sm:p-8">
            <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">{title}</div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function ToggleRow({ label, value, onChange, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={() => onChange(!value)}
            className={`w-full flex items-center justify-between rounded-2xl border px-4 py-3 transition-all ${
                value ? "border-violet-200 bg-violet-50/60" : "border-black/5 bg-white hover:bg-neutral-50"
            }`}
        >
            <span className="text-[13.5px] text-black">{label}</span>
            <span className={`inline-flex h-5 w-9 rounded-full transition-all ${value ? "bg-violet-500" : "bg-neutral-200"}`}>
                <span className={`h-4 w-4 rounded-full bg-white shadow transition-all mt-0.5 ${value ? "translate-x-4" : "translate-x-0.5"}`} />
            </span>
        </button>
    );
}
